import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Filter, Plus, Edit2, Trash2, Loader2, Grid3X3, Store, LayoutDashboard, RefreshCcw, CreditCard as PlateIcon, Timer, ChevronRight, ShieldOff, Edit, ListChecks, X, Building2, Ban } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import BanReasonsManager from './manage-platform/BanReasonsManager';
import { Input } from '../components/ui/input';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import DashboardHeader from '../components/DashboardHeader';
import BreadcrumbNav from '../components/BreadcrumbNav';
import { TranslateButton } from '../components/TranslateButton';
import { toast } from 'sonner';
import axios from 'axios';
import { usePerms } from '../permissions/PermissionsContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ControlCloudGMBusiness() {
  const navigate = useNavigate();
  const { canViewRoute } = usePerms();
  const { language } = useLanguage();
  const { token } = useAuth();
  // activeView synced with URL (?view=dropdown-lists) so browser Back works
  const [searchParams, setSearchParams] = useSearchParams();
  const activeView = searchParams.get('view') || 'main';
  const setActiveView = (v) => {
    if (!v || v === 'main') setSearchParams({});
    else setSearchParams({ view: v });
  };
  // Feb 2026: `null` = show the 4-card sub-hub, otherwise show the reasons
  // list for the picked tab. Legacy tabs: edit_request / rejection / agency_rejection.
  const [dropdownListsTab, setDropdownListsTab] = useState(null);
  const [dropdownReasons, setDropdownReasons] = useState([]);
  const [loadingReasons, setLoadingReasons] = useState(false);
  const [showAddReasonModal, setShowAddReasonModal] = useState(false);
  const [showEditReasonModal, setShowEditReasonModal] = useState(false);
  const [editingReason, setEditingReason] = useState(null);
  const [newReasonAr, setNewReasonAr] = useState('');
  const [newReasonEn, setNewReasonEn] = useState('');
  const [savingReason, setSavingReason] = useState(false);
  const [deletingReason, setDeletingReason] = useState(null);

  const getHeaders = () => ({
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  });

  const fetchDropdownReasons = async (type) => {
    setLoadingReasons(true);
    try {
      const res = await axios.get(
        `${BACKEND_URL}/api/admin/dropdown-reasons/${type}`,
        { headers: getHeaders() }
      );
      setDropdownReasons(res.data.data || []);
    } catch (err) {
      console.error('Error fetching reasons:', err);
      toast.error(language === 'ar' ? 'فشل في جلب البيانات' : 'Failed to fetch data');
    } finally {
      setLoadingReasons(false);
    }
  };

  const addReason = async () => {
    if (!newReasonAr.trim()) {
      toast.error(language === 'ar' ? 'يرجى إدخال السبب بالعربية' : 'Please enter reason in Arabic');
      return;
    }
    setSavingReason(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/admin/dropdown-reasons/${dropdownListsTab}`,
        {
          label_ar: newReasonAr.trim(),
          label_en: newReasonEn.trim() || newReasonAr.trim()
        },
        { headers: getHeaders() }
      );
      toast.success(language === 'ar' ? 'تمت الإضافة بنجاح' : 'Added successfully');
      setShowAddReasonModal(false);
      setNewReasonAr('');
      setNewReasonEn('');
      fetchDropdownReasons(dropdownListsTab);
    } catch (err) {
      toast.error(language === 'ar' ? 'فشل في الإضافة' : 'Failed to add');
    } finally {
      setSavingReason(false);
    }
  };

  const updateReason = async () => {
    if (!editingReason || !newReasonAr.trim()) return;
    setSavingReason(true);
    try {
      await axios.put(
        `${BACKEND_URL}/api/admin/dropdown-reasons/${editingReason.id}`,
        {
          label_ar: newReasonAr.trim(),
          label_en: newReasonEn.trim() || newReasonAr.trim()
        },
        { headers: getHeaders() }
      );
      toast.success(language === 'ar' ? 'تم التحديث بنجاح' : 'Updated successfully');
      setShowEditReasonModal(false);
      setEditingReason(null);
      setNewReasonAr('');
      setNewReasonEn('');
      fetchDropdownReasons(dropdownListsTab);
    } catch (err) {
      toast.error(language === 'ar' ? 'فشل في التحديث' : 'Failed to update');
    } finally {
      setSavingReason(false);
    }
  };

  const deleteReason = async (id) => {
    setDeletingReason(id);
    try {
      await axios.delete(
        `${BACKEND_URL}/api/admin/dropdown-reasons/${id}`,
        { headers: getHeaders() }
      );
      toast.success(language === 'ar' ? 'تم الحذف بنجاح' : 'Deleted successfully');
      fetchDropdownReasons(dropdownListsTab);
    } catch (err) {
      toast.error(language === 'ar' ? 'فشل في الحذف' : 'Failed to delete');
    } finally {
      setDeletingReason(null);
    }
  };

  // Scroll to top on view change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [activeView]);

  useEffect(() => {
    if (activeView === 'dropdown-lists' && dropdownListsTab) {
      fetchDropdownReasons(dropdownListsTab);
    }
  }, [activeView, dropdownListsTab]);

  // Dynamic breadcrumb based on activeView
  const getBreadcrumbItems = () => {
    // المسار: التحكم والمعلومات للمنصات > CLOUDGM FZ LLC (للتحكم) > التحكم والمعلومات (منصة التجار)
    const baseItems = [
      { label_ar: 'لوحة التحكم', label_en: 'Dashboard', path: '/dashboard' },
      { label_ar: 'التحكم والمعلومات للمنصات', label_en: 'Platform Control & Info', path: '/dashboard?view=platform-control' },
      { label_ar: 'CLOUDGM FZ LLC (للتحكم)', label_en: 'CLOUDGM FZ LLC (Control)', path: '/dashboard?view=platform-control&sub=fzlc' },
    ];

    if (activeView === 'dropdown-lists') {
      const tabLabels = {
        edit_request:     { ar: 'أسباب طلب التعديل',        en: 'Edit Request Reasons' },
        rejection:        { ar: 'أسباب الرفض',                en: 'Rejection Reasons' },
        agency_rejection: { ar: 'أسباب رفض طلب الوكالة',      en: 'Agency Rejection Reasons' },
      };
      const crumbs = [
        ...baseItems,
        { label_ar: 'التحكم والمعلومات (منصة التجار)', label_en: 'Control & Info (Merchants)', path: '/control/cloudgm-business' },
        { label_ar: 'القوائم المنسدلة للتحكم بمنصة التجار', label_en: 'Merchant Platform Dropdown Lists',
          path: dropdownListsTab ? '/control/cloudgm-business?view=dropdown-lists' : null },
      ];
      if (dropdownListsTab && tabLabels[dropdownListsTab]) {
        crumbs.push({
          label_ar: tabLabels[dropdownListsTab].ar,
          label_en: tabLabels[dropdownListsTab].en,
          path: null,
        });
      }
      return crumbs;
    }
    
    return [
      ...baseItems,
      { label_ar: 'التحكم والمعلومات (منصة التجار)', label_en: 'Control & Info (Merchants)', path: null }
    ];
  };

  const handleBack = () => {
    if (activeView === 'main') {
      navigate('/dashboard?view=platform-control&sub=fzlc');  // Go back to platform control FZLC selection
    } else {
      setActiveView('main');
    }
  };

  return (
    <div className="min-h-screen bg-[#030303]">
      {/* Background Effects */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[500px] bg-[#d4af37]/5 rounded-full blur-[150px]" />
        <div className="absolute bottom-0 right-0 w-[400px] h-[400px] bg-purple-500/3 rounded-full blur-[100px]" />
      </div>

      {/* Unified Header */}
      <DashboardHeader
        title={language === 'ar' ? 'منصة التجار' : 'Merchant Platform'}
        subtitle="CLOUDGM BUSINESS"
        icon={Store}
        iconColor="#8B5CF6"
        onBack={handleBack}
      />

      {/* Breadcrumb with border */}
      <div className="border-b border-white/5">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <BreadcrumbNav items={getBreadcrumbItems()} />
        </div>
      </div>

      {/* Main Content */}
      <main className="relative z-10 py-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Main View - Control Options */}
        {activeView === 'main' && (
          <>
            {/* Page Title */}
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center mb-8"
            >
              <h1 className="text-2xl font-bold text-white">
                {language === 'ar' ? 'التحكم والمعلومات بمنصة التجار' : 'Merchants Platform Control & Info'}
              </h1>
            </motion.div>

            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
            >
            {/* إدارة تصنيفات المتاجر */}
            {canViewRoute('/control/cloudgm-business/categories') && (
            <motion.div
              whileHover={{ y: -5 }}
              onClick={() => navigate('/control/cloudgm-business/categories')}
              className="group relative bg-white/[0.02] backdrop-blur-xl rounded-2xl p-6 border border-white/10 hover:border-emerald-500/40 transition-all duration-300 cursor-pointer text-center"
              data-testid="ctrl-categories"
            >
              {/* Screen Code Badge */}
              <div className="absolute top-3 left-3 px-2 py-0.5 bg-emerald-500/10 rounded-md">
                <span className="text-[10px] font-mono text-emerald-400">BIZ-C01</span>
              </div>
              <div className="w-14 h-14 bg-emerald-500/10 rounded-xl flex items-center justify-center mx-auto mb-3 group-hover:scale-110 transition-transform duration-300 border border-emerald-500/20">
                <Grid3X3 className="h-8 w-8 text-emerald-400" />
              </div>
              <h3 className="text-base font-bold text-white mb-1">
                {language === 'ar' ? 'إدارة تصنيفات المتاجر' : 'Store Categories Management'}
              </h3>
              <p className="text-xs text-[#71717A]">
                {language === 'ar' ? 'إدارة التصنيفات الرئيسية والفرعية' : 'Manage main and subcategories'}
              </p>
            </motion.div>
            )}

            {/* Feb 2026 — renamed from "القائمة المنسدلة لطلب المتاجر".
                Inside this view we now show a hub of 4 sub-buttons (3 legacy
                reason lists + 1 new "مسمى الخدمة لإدخال المهام (يدوي)"). */}
            {canViewRoute('/control/cloudgm-business?tab=dropdown-lists') && (
            <motion.div
              whileHover={{ y: -5 }}
              onClick={() => setActiveView('dropdown-lists')}
              className="group relative bg-white/[0.02] backdrop-blur-xl rounded-2xl p-6 border border-white/10 hover:border-rose-500/40 transition-all duration-300 cursor-pointer text-center"
              data-testid="ctrl-dropdown-lists"
            >
              {/* Screen Code Badge */}
              <div className="absolute top-3 left-3 px-2 py-0.5 bg-rose-500/10 rounded-md">
                <span className="text-[10px] font-mono text-rose-400">BIZ-C02</span>
              </div>
              <div className="w-14 h-14 bg-rose-500/10 rounded-xl flex items-center justify-center mx-auto mb-3 group-hover:scale-110 transition-transform duration-300 border border-rose-500/20">
                <Filter className="h-8 w-8 text-rose-400" />
              </div>
              <h3 className="text-base font-bold text-white mb-1">
                {language === 'ar' ? 'القوائم المنسدلة للتحكم بمنصة التجار' : 'Merchant Platform Dropdown Lists'}
              </h3>
              <p className="text-xs text-[#71717A]">
                {language === 'ar' ? 'أسباب الرفض والتعديل + مسميات المهام اليدوية' : 'Rejection & edit reasons + manual-task service names'}
              </p>
            </motion.div>
            )}

            {/* التحكم بأنواع المتاجر */}
            {canViewRoute('/control/cloudgm-business/store-types') && (
            <motion.div
              whileHover={{ y: -5 }}
              onClick={() => navigate('/control/cloudgm-business/store-types')}
              className="group relative bg-white/[0.02] backdrop-blur-xl rounded-2xl p-6 border border-white/10 hover:border-purple-500/40 transition-all duration-300 cursor-pointer text-center"
              data-testid="ctrl-store-types"
            >
              {/* Screen Code Badge */}
              <div className="absolute top-3 left-3 px-2 py-0.5 bg-purple-500/10 rounded-md">
                <span className="text-[10px] font-mono text-purple-400">BIZ-C03</span>
              </div>
              <div className="w-14 h-14 bg-purple-500/10 rounded-xl flex items-center justify-center mx-auto mb-3 group-hover:scale-110 transition-transform duration-300 border border-purple-500/20">
                <Store className="h-8 w-8 text-purple-400" />
              </div>
              <h3 className="text-base font-bold text-white mb-1">
                {language === 'ar' ? 'التحكم بأنواع المتاجر' : 'Store Types Control'}
              </h3>
              <p className="text-xs text-[#71717A]">
                {language === 'ar' ? 'إدارة وتفعيل أنواع المتاجر المتاحة' : 'Manage available store types'}
              </p>
            </motion.div>
            )}

            {/* مزامنة منصة التجار (Integration observability) */}
            {canViewRoute('/integrations/merchant-platform') && (
            <motion.div
              whileHover={{ y: -5 }}
              onClick={() => navigate('/integrations/merchant-platform')}
              className="group relative bg-white/[0.02] backdrop-blur-xl rounded-2xl p-6 border border-white/10 hover:border-cyan-500/40 transition-all duration-300 cursor-pointer text-center"
              data-testid="ctrl-merchant-sync"
            >
              {/* Screen Code Badge */}
              <div className="absolute top-3 left-3 px-2 py-0.5 bg-cyan-500/10 rounded-md">
                <span className="text-[10px] font-mono text-cyan-400">INT-MP01</span>
              </div>
              <div className="w-14 h-14 bg-cyan-500/10 rounded-xl flex items-center justify-center mx-auto mb-3 group-hover:scale-110 transition-transform duration-300 border border-cyan-500/20">
                <RefreshCcw className="h-8 w-8 text-cyan-400" />
              </div>
              <h3 className="text-base font-bold text-white mb-1">
                {language === 'ar' ? 'مزامنة منصة التجار' : 'Merchant Platform Sync'}
              </h3>
              <p className="text-xs text-[#71717A]">
                {language === 'ar' ? 'مراقبة استهلاك الـ APIs وتحديث الـ Cache' : 'Monitor API usage & cache invalidation'}
              </p>
            </motion.div>
            )}

            {/* التحكم بأكواد لوحات السيارات */}
            {canViewRoute('/control/cloudgm-business/plate-codes') && (
            <motion.div
              whileHover={{ y: -5 }}
              onClick={() => navigate('/control/cloudgm-business/plate-codes')}
              className="group relative bg-white/[0.02] backdrop-blur-xl rounded-2xl p-6 border border-white/10 hover:border-amber-500/40 transition-all duration-300 cursor-pointer text-center"
              data-testid="ctrl-plate-codes"
            >
              {/* Screen Code Badge */}
              <div className="absolute top-3 left-3 px-2 py-0.5 bg-amber-500/10 rounded-md">
                <span className="text-[10px] font-mono text-amber-400">BIZ-C04</span>
              </div>
              <div className="w-14 h-14 bg-amber-500/10 rounded-xl flex items-center justify-center mx-auto mb-3 group-hover:scale-110 transition-transform duration-300 border border-amber-500/20">
                <PlateIcon className="h-8 w-8 text-amber-400" />
              </div>
              <h3 className="text-base font-bold text-white mb-1">
                {language === 'ar' ? 'التحكم بأكواد لوحات السيارات' : 'Plate Codes Control'}
              </h3>
              <p className="text-xs text-[#71717A]">
                {language === 'ar' ? '7 إمارات × 3 فئات (خصوصي/دراجة/كلاسيك) + الأكواد' : '7 emirates × 3 categories + codes'}
              </p>
            </motion.div>
            )}

            {/* التحكم بمدة تقديم طلب الوكيل — Agent Reapply Cooldown */}
            {canViewRoute('/control/cloudgm-business/agent-cooldown') && (
            <motion.div
              whileHover={{ y: -5 }}
              onClick={() => navigate('/control/cloudgm-business/agent-cooldown')}
              className="group relative bg-white/[0.02] backdrop-blur-xl rounded-2xl p-6 border border-white/10 hover:border-orange-500/40 transition-all duration-300 cursor-pointer text-center"
              data-testid="ctrl-agent-cooldown"
            >
              {/* Screen Code Badge */}
              <div className="absolute top-3 left-3 px-2 py-0.5 bg-orange-500/10 rounded-md">
                <span className="text-[10px] font-mono text-orange-400">BIZ-C05</span>
              </div>
              <div className="w-14 h-14 bg-orange-500/10 rounded-xl flex items-center justify-center mx-auto mb-3 group-hover:scale-110 transition-transform duration-300 border border-orange-500/20">
                <Timer className="h-8 w-8 text-orange-400" />
              </div>
              <h3 className="text-base font-bold text-white mb-1">
                {language === 'ar' ? 'التحكم بمدة تقديم طلب الوكيل' : 'Agent Reapply Cooldown'}
              </h3>
              <p className="text-xs text-[#71717A]">
                {language === 'ar' ? 'مدة الانتظار بعد رفض طلب وكيل قبل السماح بإعادة التقديم' : 'How long a rejected agent must wait before reapplying'}
              </p>
            </motion.div>
            )}

            </motion.div>
          </>
        )}

        {/* Dropdown Lists View */}
        {activeView === 'dropdown-lists' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                <Filter className="h-6 w-6 text-rose-400" />
                {language === 'ar' ? 'القوائم المنسدلة للتحكم بمنصة التجار' : 'Merchant Platform Dropdown Lists'}
              </h2>
              {dropdownListsTab && (
                <Button
                  onClick={() => setDropdownListsTab(null)}
                  variant="outline"
                  className="border-white/10 bg-transparent text-white h-9"
                  data-testid="btn-back-dropdown-hub"
                >
                  <ChevronRight className={`h-4 w-4 ${language === 'ar' ? '' : 'rotate-180'} me-1`} />
                  {language === 'ar' ? 'رجوع للقوائم' : 'Back to hub'}
                </Button>
              )}
            </div>

            {/* iter-247 (Feb 2026) — Manual Task Service Name button —
                MOVED here from the customer hub. Manual agent tasks
                belong semantically under the merchant/agent domain,
                not the customer domain. The 3 legacy reason lists
                stay; the "Main & Sub Services" card is the 4th. */}
            {!dropdownListsTab ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                <button
                  onClick={() => setDropdownListsTab('edit_request')}
                  className="group text-start p-6 rounded-2xl bg-white/[0.02] border border-white/10 hover:border-blue-500/40 transition-all duration-300"
                  data-testid="dd-card-edit-request"
                >
                  <div className="w-12 h-12 rounded-xl bg-blue-500/10 border border-blue-500/20 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                    <Edit className="h-6 w-6 text-blue-400" />
                  </div>
                  <h3 className="text-base font-bold text-white mb-1">
                    {language === 'ar' ? 'أسباب طلب التعديل' : 'Edit Request Reasons'}
                  </h3>
                  <p className="text-xs text-[#71717A]">
                    {language === 'ar' ? 'قائمة الأسباب المستخدمة عند طلب تعديل من المتجر' : 'Reasons used when requesting an edit'}
                  </p>
                </button>

                <button
                  onClick={() => setDropdownListsTab('rejection')}
                  className="group text-start p-6 rounded-2xl bg-white/[0.02] border border-white/10 hover:border-red-500/40 transition-all duration-300"
                  data-testid="dd-card-rejection"
                >
                  <div className="w-12 h-12 rounded-xl bg-red-500/10 border border-red-500/20 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                    <X className="h-6 w-6 text-red-400" />
                  </div>
                  <h3 className="text-base font-bold text-white mb-1">
                    {language === 'ar' ? 'أسباب الرفض' : 'Rejection Reasons'}
                  </h3>
                  <p className="text-xs text-[#71717A]">
                    {language === 'ar' ? 'أسباب رفض طلبات المتاجر' : 'Reasons for rejecting store requests'}
                  </p>
                </button>

                <button
                  onClick={() => setDropdownListsTab('agency_rejection')}
                  className="group text-start p-6 rounded-2xl bg-white/[0.02] border border-white/10 hover:border-amber-500/40 transition-all duration-300"
                  data-testid="dd-card-agency-rejection"
                >
                  <div className="w-12 h-12 rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                    <ShieldOff className="h-6 w-6 text-amber-400" />
                  </div>
                  <h3 className="text-base font-bold text-white mb-1">
                    {language === 'ar' ? 'أسباب رفض طلب الوكالة' : 'Agency Rejection Reasons'}
                  </h3>
                  <p className="text-xs text-[#71717A]">
                    {language === 'ar' ? 'أسباب رفض تسجيل الوكيل' : 'Reasons for rejecting agency registration'}
                  </p>
                </button>

                {canViewRoute('/control/cloudgm-business/merchant-dropdowns') && (
                <button
                  onClick={() => navigate('/control/cloudgm-business/merchant-dropdowns')}
                  className="group text-start p-6 rounded-2xl bg-white/[0.02] border border-white/10 hover:border-purple-500/40 transition-all duration-300"
                  data-testid="dd-card-manual-task-services"
                >
                  <div className="w-12 h-12 rounded-xl bg-purple-500/10 border border-purple-500/20 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                    <ListChecks className="h-6 w-6 text-purple-400" />
                  </div>
                  <h3 className="text-base font-bold text-white mb-1">
                    {language === 'ar' ? 'مسمى الخدمة لإدخال المهام (يدوي)' : 'Manual Task Entry Service Name'}
                  </h3>
                  <p className="text-xs text-[#71717A]">
                    {language === 'ar' ? 'مسميات الخدمات (رئيسي/فرعي) اللي تظهر في فورم "إدراج مهمة لوكيل (يدوي)"' : 'Service names (main + sub) shown in the manual agent task form'}
                  </p>
                </button>
                )}

                <button
                  onClick={() => setDropdownListsTab('merchant_ban_reason')}
                  className="group text-start p-6 rounded-2xl bg-white/[0.02] border border-white/10 hover:border-red-500/40 transition-all duration-300"
                  data-testid="dd-card-merchant-ban-reason"
                >
                  <div className="w-12 h-12 rounded-xl bg-red-500/10 border border-red-500/20 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                    <Ban className="h-6 w-6 text-red-400" />
                  </div>
                  <h3 className="text-base font-bold text-white mb-1">
                    {language === 'ar' ? 'أسباب حظر الحساب (منصة التجار)' : 'Merchant Ban Reasons'}
                  </h3>
                  <p className="text-xs text-[#71717A]">
                    {language === 'ar'
                      ? 'قائمة الأسباب التي تظهر عند حظر تاجر — يمكن للأدمن إضافة وترجمة أسباب جديدة.'
                      : 'Reasons shown when blocking a merchant — admin can add and translate new ones.'}
                  </p>
                </button>

                {/* iter-253 (Feb 2026) — Removed the duplicate "قوائم خدمات الشركات
                    لملفات الشركات" card from here. It now lives ONLY under
                    "إدارة الشركة CLOUDGM FZ-LLC" (FZLCManagementHub). */}
              </div>
            ) : dropdownListsTab === 'merchant_ban_reason' ? (
              <BanReasonsManager
                reasonType="merchant_ban_reason"
                language={language}
                title={language === 'ar' ? 'أسباب حظر الحساب (منصة التجار)' : 'Merchant Ban Reasons'}
                accentColor="red"
              />
            ) : (
              <>
                {/* Add Button */}
                <div className="mb-4">
                  <Button
                    onClick={() => setShowAddReasonModal(true)}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl"
                    data-testid="btn-add-reason"
                  >
                    <Plus className="h-4 w-4 me-2" />
                    {language === 'ar' ? 'إضافة سبب جديد' : 'Add New Reason'}
                  </Button>
                </div>

            {/* Reasons List */}
            <div className="bg-[#1a1a2e] rounded-2xl border border-[#2a2a4e] overflow-hidden">
              {loadingReasons ? (
                <div className="flex items-center justify-center py-16">
                  <Loader2 className="h-8 w-8 animate-spin text-[#c9a227]" />
                </div>
              ) : dropdownReasons.length === 0 ? (
                <div className="text-center py-16 text-[#808080]">
                  <Filter className="h-12 w-12 mx-auto mb-3 opacity-30" />
                  <p>{language === 'ar' ? 'لا توجد أسباب مضافة' : 'No reasons added'}</p>
                </div>
              ) : (
                <div className="divide-y divide-[#2a2a4e]">
                  {dropdownReasons.map((reason, index) => (
                    <div key={reason.id || index} className="p-4 flex items-center justify-between hover:bg-[#0a0a0a] transition-colors">
                      <div>
                        <p className="text-white font-medium">{reason.label_ar}</p>
                        {reason.label_en && reason.label_en !== reason.label_ar && (
                          <p className="text-sm text-[#808080]">{reason.label_en}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          onClick={() => {
                            setEditingReason(reason);
                            setNewReasonAr(reason.label_ar);
                            setNewReasonEn(reason.label_en || '');
                            setShowEditReasonModal(true);
                          }}
                          variant="ghost"
                          size="sm"
                          className="text-blue-400 hover:text-blue-300 hover:bg-blue-500/10"
                        >
                          <Edit2 className="h-4 w-4" />
                        </Button>
                        <Button
                          onClick={() => deleteReason(reason.id)}
                          disabled={deletingReason === reason.id}
                          variant="ghost"
                          size="sm"
                          className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                        >
                          {deletingReason === reason.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Trash2 className="h-4 w-4" />
                          )}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
              </>
            )}
          </motion.div>
        )}
        </div>
      </main>

      {/* Add Reason Modal */}
      <Dialog open={showAddReasonModal} onOpenChange={setShowAddReasonModal}>
        <DialogContent className="bg-[#1a1a2e] border-[#2a2a4e] text-white" data-testid="reason-add-modal">
          <DialogHeader>
            <DialogTitle className="text-white">
              {language === 'ar' ? 'إضافة سبب جديد' : 'Add New Reason'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm text-[#808080] mb-2 block">
                {language === 'ar' ? 'السبب بالعربية *' : 'Reason in Arabic *'}
              </label>
              <div className="flex items-center gap-2">
                <Input
                  value={newReasonAr}
                  onChange={(e) => setNewReasonAr(e.target.value.replace(/[A-Za-z]+/g, ''))}
                  placeholder={language === 'ar' ? 'أدخل السبب بالعربية' : 'Enter reason in Arabic'}
                  dir="rtl"
                  className="bg-[#0a0a0a] border-[#2a2a4e] text-white flex-1"
                  data-testid="reason-add-ar-input"
                />
                <TranslateButton
                  sourceText={newReasonAr}
                  direction="ar_to_en"
                  onTranslated={setNewReasonEn}
                  testId="add-reason-translate-ar-to-en"
                />
              </div>
              <p className="text-[10px] text-[#606060] mt-1">{language === 'ar' ? 'حروف عربية فقط — الحروف اللاتينية مرفوضة تلقائياً' : 'Arabic only — Latin letters are auto-stripped'}</p>
            </div>
            <div>
              <label className="text-sm text-[#808080] mb-2 block">
                {language === 'ar' ? 'السبب بالإنجليزية *' : 'Reason in English *'}
              </label>
              <div className="flex items-center gap-2">
                <Input
                  value={newReasonEn}
                  onChange={(e) => setNewReasonEn(e.target.value.replace(/[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF]+/g, ''))}
                  placeholder={language === 'ar' ? 'أدخل السبب بالإنجليزية' : 'Enter reason in English'}
                  dir="ltr"
                  className="bg-[#0a0a0a] border-[#2a2a4e] text-white flex-1"
                  data-testid="reason-add-en-input"
                />
                <TranslateButton
                  sourceText={newReasonEn}
                  direction="en_to_ar"
                  onTranslated={setNewReasonAr}
                  testId="add-reason-translate-en-to-ar"
                />
              </div>
              <p className="text-[10px] text-[#606060] mt-1">{language === 'ar' ? 'حروف إنجليزية فقط — الحروف العربية مرفوضة تلقائياً' : 'English only — Arabic letters are auto-stripped'}</p>
            </div>
          </div>
          <DialogFooter>
            <Button
              onClick={() => setShowAddReasonModal(false)}
              variant="outline"
              className="border-[#2a2a4e] text-white"
              data-testid="reason-add-cancel-btn"
            >
              {language === 'ar' ? 'إلغاء' : 'Cancel'}
            </Button>
            <Button
              onClick={addReason}
              disabled={savingReason || !newReasonAr.trim() || !newReasonEn.trim()}
              className="bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-40"
              data-testid="reason-add-submit-btn"
            >
              {savingReason ? <Loader2 className="h-4 w-4 animate-spin" /> : (language === 'ar' ? 'إضافة' : 'Add')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Reason Modal */}
      <Dialog open={showEditReasonModal} onOpenChange={setShowEditReasonModal}>
        <DialogContent className="bg-[#1a1a2e] border-[#2a2a4e] text-white" data-testid="reason-edit-modal">
          <DialogHeader>
            <DialogTitle className="text-white">
              {language === 'ar' ? 'تعديل السبب' : 'Edit Reason'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm text-[#808080] mb-2 block">
                {language === 'ar' ? 'السبب بالعربية *' : 'Reason in Arabic *'}
              </label>
              <div className="flex items-center gap-2">
                <Input
                  value={newReasonAr}
                  onChange={(e) => setNewReasonAr(e.target.value.replace(/[A-Za-z]+/g, ''))}
                  dir="rtl"
                  className="bg-[#0a0a0a] border-[#2a2a4e] text-white flex-1"
                  data-testid="reason-edit-ar-input"
                />
                <TranslateButton
                  sourceText={newReasonAr}
                  direction="ar_to_en"
                  onTranslated={setNewReasonEn}
                  testId="edit-reason-translate-ar-to-en"
                />
              </div>
              <p className="text-[10px] text-[#606060] mt-1">{language === 'ar' ? 'حروف عربية فقط — الحروف اللاتينية مرفوضة تلقائياً' : 'Arabic only — Latin letters are auto-stripped'}</p>
            </div>
            <div>
              <label className="text-sm text-[#808080] mb-2 block">
                {language === 'ar' ? 'السبب بالإنجليزية *' : 'Reason in English *'}
              </label>
              <div className="flex items-center gap-2">
                <Input
                  value={newReasonEn}
                  onChange={(e) => setNewReasonEn(e.target.value.replace(/[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF]+/g, ''))}
                  dir="ltr"
                  className="bg-[#0a0a0a] border-[#2a2a4e] text-white flex-1"
                  data-testid="reason-edit-en-input"
                />
                <TranslateButton
                  sourceText={newReasonEn}
                  direction="en_to_ar"
                  onTranslated={setNewReasonAr}
                  testId="edit-reason-translate-en-to-ar"
                />
              </div>
              <p className="text-[10px] text-[#606060] mt-1">{language === 'ar' ? 'حروف إنجليزية فقط — الحروف العربية مرفوضة تلقائياً' : 'English only — Arabic letters are auto-stripped'}</p>
            </div>
          </div>
          <DialogFooter>
            <Button
              onClick={() => setShowEditReasonModal(false)}
              variant="outline"
              className="border-[#2a2a4e] text-white"
              data-testid="reason-edit-cancel-btn"
            >
              {language === 'ar' ? 'إلغاء' : 'Cancel'}
            </Button>
            <Button
              onClick={updateReason}
              disabled={savingReason || !newReasonAr.trim() || !newReasonEn.trim()}
              className="bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-40"
              data-testid="reason-edit-submit-btn"
            >
              {savingReason ? <Loader2 className="h-4 w-4 animate-spin" /> : (language === 'ar' ? 'حفظ' : 'Save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
